import { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import Papa from "papaparse";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { levenshtein } from "@/components/atomic-crm/import-engine/dedup";
import { getPrimaryEmail } from "@/components/atomic-crm/providers/supabase/contactAdapter";

interface Props {
    onClose: () => void;
    onComplete: () => void;
}

export function ConnectionUploadDialog({ onClose, onComplete }: Props) {
    const [teamMembers, setTeamMembers] = useState<
        Array<{ id: number; first_name: string; last_name: string }>
    >([]);
    const [selectedMember, setSelectedMember] = useState<string>("");
    const [status, setStatus] = useState<
        "select" | "uploading" | "matching" | "done"
    >("select");
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState({ uploaded: 0, matched: 0 });

    useEffect(() => {
        supabase
            .from("sales")
            .select("id, first_name, last_name")
            .eq("disabled", false)
            .then(({ data }) => {
                if (data) setTeamMembers(data);
            });
    }, []);

    const handleUpload = async (file: File) => {
        if (!selectedMember) return;
        const memberId = parseInt(selectedMember);

        setStatus("uploading");

        // Parse CSV
        const parsed = await new Promise<
            Papa.ParseResult<Record<string, string>>
        >((resolve) => {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: resolve,
            });
        });

        const rows = parsed.data;

        // Create upload record
        const { data: upload } = await supabase
            .from("connection_uploads")
            .insert({
                team_member_id: memberId,
                filename: file.name,
                connection_count: rows.length,
                matched_count: 0,
            })
            .select()
            .single();

        // Insert connections
        const connections = rows.map((row) => ({
            team_member_id: memberId,
            first_name: row["First Name"] || "",
            last_name: row["Last Name"] || "",
            linkedin_url: row["URL"] || "",
            email: row["Email Address"] || null,
            company: row["Company"] || "",
            title: row["Position"] || "",
            connection_degree: 1,
            upload_id: upload?.id,
            connected_on: row["Connected On"]
                ? new Date(row["Connected On"]).toISOString().split("T")[0]
                : null,
        }));

        // Batch insert in chunks of 100
        for (let i = 0; i < connections.length; i += 100) {
            await supabase
                .from("linkedin_connections")
                .insert(connections.slice(i, i + 100));
            setProgress(((i + 100) / connections.length) * 50);
        }

        setStatus("matching");

        // Load existing contacts for matching
        const { data: contacts } = await supabase
            .from("contacts")
            .select(
                "id, first_name, last_name, linkedin_url, email_jsonb, company_id, companies(name)",
            );
        let matchedCount = 0;

        // Match each connection to contacts
        const { data: insertedConnections } = await supabase
            .from("linkedin_connections")
            .select("id, first_name, last_name, linkedin_url, email, company")
            .eq("upload_id", upload?.id)
            .is("contact_id", null);

        if (insertedConnections && contacts) {
            for (let i = 0; i < insertedConnections.length; i++) {
                const conn = insertedConnections[i];
                let matchedContactId: number | null = null;

                // Match on LinkedIn URL (most reliable)
                if (conn.linkedin_url) {
                    const urlMatch = contacts.find(
                        (c: any) =>
                            c.linkedin_url &&
                            c.linkedin_url.toLowerCase() ===
                                conn.linkedin_url.toLowerCase(),
                    );
                    if (urlMatch) matchedContactId = urlMatch.id;
                }

                // Match on email
                if (!matchedContactId && conn.email) {
                    const emailMatch = contacts.find((c: any) => {
                        const email = getPrimaryEmail(c.email_jsonb);
                        return (
                            email &&
                            email.toLowerCase() === conn.email!.toLowerCase()
                        );
                    });
                    if (emailMatch) matchedContactId = emailMatch.id;
                }

                // Match on name + company (fuzzy)
                if (!matchedContactId && conn.first_name && conn.last_name) {
                    const nameMatch = contacts.find(
                        (c: any) =>
                            c.first_name?.toLowerCase() ===
                                conn.first_name.toLowerCase() &&
                            c.last_name?.toLowerCase() ===
                                conn.last_name.toLowerCase() &&
                            c.companies?.name &&
                            levenshtein(c.companies.name, conn.company) <= 2,
                    );
                    if (nameMatch) matchedContactId = nameMatch.id;
                }

                if (matchedContactId) {
                    await supabase
                        .from("linkedin_connections")
                        .update({ contact_id: matchedContactId })
                        .eq("id", conn.id);
                    matchedCount++;
                }

                setProgress(
                    50 + ((i + 1) / insertedConnections.length) * 50,
                );
            }
        }

        // Update upload record
        if (upload) {
            await supabase
                .from("connection_uploads")
                .update({ matched_count: matchedCount })
                .eq("id", upload.id);
        }

        setResult({ uploaded: connections.length, matched: matchedCount });
        setStatus("done");
    };

    return (
        <Dialog
            open
            onOpenChange={() => {
                if (status === "done") onComplete();
                else onClose();
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Upload LinkedIn Connections</DialogTitle>
                </DialogHeader>
                {status === "select" && (
                    <div className="space-y-4">
                        <div>
                            <label className="mb-1 block text-sm font-medium">
                                Team Member
                            </label>
                            <Select
                                value={selectedMember}
                                onValueChange={setSelectedMember}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select team member" />
                                </SelectTrigger>
                                <SelectContent>
                                    {teamMembers.map((m) => (
                                        <SelectItem
                                            key={m.id}
                                            value={String(m.id)}
                                        >
                                            {m.first_name} {m.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium">
                                LinkedIn Export CSV
                            </label>
                            <input
                                type="file"
                                accept=".csv"
                                className="w-full text-sm"
                                disabled={!selectedMember}
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleUpload(f);
                                }}
                            />
                        </div>
                    </div>
                )}
                {(status === "uploading" || status === "matching") && (
                    <div className="space-y-3">
                        <p className="text-sm">
                            {status === "uploading"
                                ? "Uploading connections..."
                                : "Matching to contacts..."}
                        </p>
                        <Progress value={progress} />
                    </div>
                )}
                {status === "done" && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="rounded-md bg-blue-50 p-3 text-center dark:bg-blue-950">
                                <div className="text-2xl font-bold text-blue-600">
                                    {result.uploaded}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Uploaded
                                </div>
                            </div>
                            <div className="rounded-md bg-green-50 p-3 text-center dark:bg-green-950">
                                <div className="text-2xl font-bold text-green-600">
                                    {result.matched}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Matched
                                </div>
                            </div>
                        </div>
                        <Button className="w-full" onClick={onComplete}>
                            Done
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
